import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { canAccessRoute } from '../../utils/rbac';

interface RequireAccessProps {
  /** The route key used in the RBAC config (e.g. "employees", "plans"). */
  routeKey: string;
  /** Optional feature permission required in addition to route access. */
  permission?: string;
  /** Where to redirect when access is denied. Defaults to /dashboard. */
  fallback?: string;
  children: React.ReactNode;
}

/**
 * Route-level access guard.
 * Reads the current user role from authStore and checks it against the RBAC
 * config. Redirects to `fallback` (default: /dashboard) when access is denied.
 */
export const RequireAccess: React.FC<RequireAccessProps> = ({
  routeKey,
  permission,
  fallback = '/dashboard',
  children,
}) => {
  const role = useAuthStore(state => state.role);
  const hasPermission = useAuthStore(state => state.hasPermission);

  if (!canAccessRoute(role, routeKey)) {
    return <Navigate to={fallback} replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};
